/**
 * Token store — same seam as frontend/web/src/lib/auth.ts. Login + refresh go
 * to the PRODUCT backend (shared JWT secret); every other call goes to the
 * notification platform. Admin role enforced server-side on /api/v1/admin/*.
 */

const REFRESH_KEY = 'om365.notify-admin.refresh-token';
const USER_KEY = 'om365.notify-admin.user';

export interface SessionUser {
  id: string;
  personId: string | null;
  name: string | null;
  role: string;
  tenantId: string;
}

let accessToken: string | null = null;
let refreshInFlight: Promise<void> | null = null;

/** Product API root — auth lives there. Dev default goes through the Vite proxy. */
const PRODUCT_API_BASE_URL = import.meta.env.VITE_PRODUCT_API_URL?.trim() || '/product-api';
export const PRODUCT_API_ROOT = `${PRODUCT_API_BASE_URL}/v1`;

export const tokenStore = {
  getAccess: () => accessToken,

  set(access: string, refresh?: string | null) {
    accessToken = access;
    if (refresh) sessionStorage.setItem(REFRESH_KEY, refresh);
  },

  clear() {
    accessToken = null;
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(USER_KEY);
  },

  async refresh(): Promise<void> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const refresh = sessionStorage.getItem(REFRESH_KEY);
        if (!refresh) throw new Error('no refresh token');
        const res = await fetch(`${PRODUCT_API_ROOT}/auth/refresh`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${refresh}` },
        });
        if (!res.ok) throw new Error('refresh failed');
        const body = (await res.json()) as { data: { accessToken: string } };
        accessToken = body.data.accessToken;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  },
};

export const sessionUser = {
  get(): SessionUser | null {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SessionUser;
    } catch {
      return null;
    }
  },
  set(user: SessionUser) {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  },
};

export async function login(loginId: string, password: string): Promise<SessionUser> {
  const res = await fetch(`${PRODUCT_API_ROOT}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginId, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error?.message ?? 'Login failed');
  }
  const body = (await res.json()) as {
    data: {
      accessToken: string;
      refreshToken: string;
      user?: SessionUser;
    } & Partial<SessionUser>;
  };
  tokenStore.set(body.data.accessToken, body.data.refreshToken);
  const user: SessionUser = body.data.user ?? {
    id: (body.data as any).id ?? '',
    personId: (body.data as any).personId ?? null,
    name: (body.data as any).name ?? null,
    role: (body.data as any).role ?? '',
    tenantId: (body.data as any).tenantId ?? '',
  };
  sessionUser.set(user);
  return user;
}

export function logout() {
  tokenStore.clear();
}
